import React, { useState } from 'react';
import { ProjectContactDto } from '../siteApi';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  Phone,
  Mail,
  Building2,
  User2,
  MessageSquareText,
  ArrowBigRight,
  Pencil,
  Trash2,
  UserCircle,
} from 'lucide-react';
import { EnumContactPreference } from '@/app/Alert/types';
import { useSiteContacts, useDeleteSiteContact } from '../hooks/useSiteContacts';
import { AddContactDialog } from './AddContactDialog';

interface SiteContactsProps {
  projectId: string;
  customerId: string;
  siteCountry?: string;
}

interface ContactTableRowProps {
  contact: ProjectContactDto;
  customerId: string;
  projectId: string;
  onEdit: (contact: ProjectContactDto) => void;
}

const decodePreference = (preference: EnumContactPreference | undefined) => {
  switch (preference) {
    case EnumContactPreference.CallOnly:
      return 'Call Only';
    case EnumContactPreference.SmsOnly:
      return 'SMS Only';
    case EnumContactPreference.SmsThenCall:
      return 'SMS Then Call';
    default:
      return 'N/A';
  }
};

const ContactTableRow: React.FC<ContactTableRowProps> = ({ contact, customerId, projectId, onEdit }) => {
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const deleteContact = useDeleteSiteContact(customerId, projectId);

  const handleDelete = async () => {
    try {
      await deleteContact.mutateAsync(contact.id || '');
      toast({
        title: 'Contact Deleted',
        description: 'The contact has been deleted successfully.',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete contact. Please try again.',
        variant: 'destructive',
      });
    }
    setShowDeleteDialog(false);
  };

  const getPreferenceIcon = (preference: EnumContactPreference | undefined) => {
    switch (preference) {
      case EnumContactPreference.CallOnly:
        return <Phone className="h-4 w-4 text-blue-400" />;
      case EnumContactPreference.SmsOnly:
        return <MessageSquareText className="h-4 w-4 text-green-400" />;
      case EnumContactPreference.SmsThenCall:
        return (
          <>
            <MessageSquareText className="h-4 w-4 text-green-400" />
            <ArrowBigRight className="h-4 w-4 text-green-400" />
            <Phone className="h-4 w-4 text-green-400" />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-start gap-2">
            <User2 className="h-4 w-4 text-gray-400 mt-0.5" />
            <div>
              <div className="font-medium">
                {contact.name} {contact.lastName}
              </div>
              {(contact.companyName || contact.contactCompany) && (
                <div className="flex items-center gap-1 text-sm text-gray-400 mt-1">
                  <Building2 className="h-3 w-3" />
                  <span>{contact.companyName || contact.contactCompany}</span>
                </div>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell>
          {contact.role && (
            <div className="flex items-center gap-2">
              <UserCircle className="h-4 w-4 text-gray-400" />
              <span>{contact.role}</span>
            </div>
          )}
        </TableCell>
        <TableCell>
          {contact.phone && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-400" />
              <a href={`tel:${contact.phone}`} className="text-blue-400 hover:underline">
                {contact.phone}
              </a>
            </div>
          )}
        </TableCell>
        <TableCell>
          {contact.email && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-400" />
              <a href={`mailto:${contact.email}`} className="text-blue-400 hover:underline">
                {contact.email}
              </a>
            </div>
          )}
        </TableCell>
        <TableCell>
          {contact.contactPreference && (
            <div className="flex items-center gap-1">
              {getPreferenceIcon(contact.contactPreference)}
              <span className="text-sm">
                {decodePreference(contact.contactPreference)}
              </span>
            </div>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onEdit(contact)}
            >
              <Pencil className="h-4 w-4" />
              <span className="sr-only">Edit</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-destructive hover:text-destructive/90"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Delete</span>
            </Button>
          </div>
        </TableCell>
      </TableRow>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
      
          </DialogHeader>      <DialogDescription>
              Are you sure you want to delete this contact? This action cannot be undone.
            </DialogDescription>
          <DialogFooter>
            <DialogClose className="rounded py-2 font-semibold border">Cancel</DialogClose>
            <DialogClose
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded py-2 font-semibold"
            >
              {deleteContact.isPending ? 'Deleting...' : 'Delete'}
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const SiteContacts: React.FC<SiteContactsProps> = ({ projectId, customerId, siteCountry }) => {
  const { data, isLoading } = useSiteContacts(customerId, projectId);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingContact, setEditingContact] = useState<ProjectContactDto | null>(null);

  const handleEdit = (contact: ProjectContactDto) => {
    setEditingContact(contact);
    setShowAddDialog(true);
  };

  if (isLoading) {
    return <div className="text-sm text-gray-400">Loading contacts...</div>;
  }

  return (
    <div className="">
      <div className="flex items-center justify-between border-b p-4">
        <div>
          <h2 className="text-xl font-semibold">Site Contacts</h2>
        </div>
        <Button
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            setShowAddDialog(true);
          }}
        >
          Add Contact
        </Button>
      </div>

      {data?.rows ? (
        data.rows.length > 0 ? (
          <div className="m-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Preference</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((contact) => (
                  <ContactTableRow
                    key={contact.id}
                    contact={contact}
                    customerId={customerId}
                    projectId={projectId}
                    onEdit={handleEdit}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="rounded-lg p-8 text-center">
            <p className="text-gray-400">No contacts found for this site.</p>
            <p className="mt-2 text-sm text-gray-500">
              Add contacts to manage who should be notified for this site.
            </p>
          </div>
        )
      ) : (
        <div className="rounded-lg p-8 text-center">
          <p className="text-gray-400">No contacts found for this site.</p>
          <p className="mt-2 text-sm text-gray-500">
            Add contacts to manage who should be notified for this site.
          </p>
        </div>
      )}

      <AddContactDialog
        open={showAddDialog}
        onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) setEditingContact(null);
        }}
        projectId={projectId}
        customerId={customerId}
        editingContact={editingContact}
        siteCountry={siteCountry}
      />
    </div>
  );
};
